import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useThemeProps } from "@mui/material";
import { forwardRef } from "react";
import { useSlotProps } from "../../base/index.js";
import { ActionBar } from "../ActionBar/index.js";
import { ActionHeader } from "../ActionHeader/index.js";
import { sectionName } from "./classes.js";
import { useFilterChildren } from "./hooks/useFilterChildren.js";
import { useSectionState } from "./hooks/useSectionState.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
import { Content } from "./components/Content.js";
import { Divider } from "./components/Divider.js";
import { LoadMore } from "./components/LoadMore.js";
import { Pinned } from "./components/Pinned.js";
import { PinnedTop } from "./components/PinnedTop.js";
import { Root } from "./components/Root.js";
const Section = forwardRef(function Section2(inProps, ref) {
  const props = useThemeProps({ name: sectionName, props: inProps });
  const {
    title,
    actions,
    pageSize,
    children,
    subtitle,
    openIcon,
    hideIcon,
    className,
    maxPinned,
    closedIcon,
    components,
    size = "tiny",
    primaryAction,
    width = "100%",
    titleClickable,
    actionBarItems,
    actionBarProps,
    componentsProps,
    height = "fit-content",
    primaryActionVisibility,
    onChange: controlledOnChange,
    expanded: expandedControlled,
    testId = "tidal-section-root",
    actionBarVisibility = "always",
    loadMoreLabel = "Load more...",
    collapseBehavior = "collapsible",
    defaultExpanded = collapseBehavior === "none",
    ...rest
  } = props;
  const { expanded, onChange, onEnterHeader, onLeaveHeader, actionBarVisible, onChangedActionBar } = useSectionState({
    actions,
    defaultExpanded,
    actionBarVisibility,
    onChange: controlledOnChange,
    expanded: expandedControlled
  });
  const { top, pinned, content, loadMore, hasMoreContent } = useFilterChildren({
    pageSize,
    expanded,
    children,
    maxPinned
  });
  const showPinnedTop = top.length > 0;
  const showPinned = pinned.length > 0;
  const paddedHeader = showPinned || showPinnedTop;
  const showActionBar = actionBarItems && actionBarItems.length > 0 && actionBarVisible;
  const ownerState = {
    ...props,
    width,
    height,
    defaultExpanded,
    collapseBehavior
  };
  const utilityClasses = useUtilityClasses(ownerState);
  const rootSlotProps = useSlotProps({
    ownerState,
    elementType: Root,
    externalForwardedProps: rest,
    className: [className, utilityClasses.root],
    externalSlotProps: componentsProps?.root,
    additionalProps: { ref, as: components?.Root, "data-testid": testId }
  });
  const pinnedTopSlotProps = useSlotProps({
    ownerState,
    elementType: PinnedTop,
    className: utilityClasses.pinnedTop,
    externalSlotProps: componentsProps?.pinnedTop,
    additionalProps: { as: components?.PinnedTop, "data-testid": "tidal-section-pinnedTop" }
  });
  const dividerSlotProps = useSlotProps({
    ownerState,
    elementType: Divider,
    className: utilityClasses.divider,
    externalSlotProps: componentsProps?.divider,
    additionalProps: { as: components?.Divider, "data-testid": "tidal-section-divider" }
  });
  const pinnedSlotProps = useSlotProps({
    ownerState,
    elementType: Pinned,
    className: utilityClasses.pinned,
    externalSlotProps: componentsProps?.pinned,
    additionalProps: { as: components?.Pinned, "data-testid": "tidal-section-pinned" }
  });
  const contentSlotProps = useSlotProps({
    ownerState,
    elementType: Content,
    className: utilityClasses.content,
    externalSlotProps: componentsProps?.content,
    additionalProps: { as: components?.Content, "data-testid": "tidal-section-content" }
  });
  const loadMoreSlotProps = useSlotProps({
    ownerState,
    elementType: LoadMore,
    className: utilityClasses.loadMore,
    externalSlotProps: componentsProps?.loadMore,
    additionalProps: {
      expanded,
      hasMoreContent,
      as: components?.LoadMore,
      contentCount: content?.length ?? 0,
      onClick: loadMore,
      pinnedCount: pinned?.length ?? 0,
      topCount: top?.length ?? 0,
      "data-testid": "tidal-section-loadMore"
    }
  });
  return /* @__PURE__ */ jsxs(Root, { ...rootSlotProps, "data-tidal": "Section", children: [
    /* @__PURE__ */ jsx(
      ActionHeader,
      {
        size,
        title,
        onChange,
        hideIcon,
        subtitle,
        expanded,
        openIcon,
        padded: paddedHeader,
        closedIcon,
        primaryAction,
        titleClickable,
        defaultExpanded,
        classes: { root: utilityClasses.header },
        disableButton: collapseBehavior === "none",
        primaryActionVisibility,
        componentsProps: {
          root: {
            onMouseEnter: onEnterHeader,
            onMouseLeave: onLeaveHeader
          }
        },
        secondaryAction: showActionBar && /* @__PURE__ */ jsx(ActionBar, { ...actionBarProps, size, onChanged: onChangedActionBar, children: actionBarItems })
      }
    ),
    showPinnedTop && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(PinnedTop, { ...pinnedTopSlotProps, children: top }),
      expanded && /* @__PURE__ */ jsx(Divider, { ...dividerSlotProps })
    ] }),
    showPinned && /* @__PURE__ */ jsx(Pinned, { ...pinnedSlotProps, children: pinned }),
    expanded && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Content, { ...contentSlotProps, children: content }),
      /* @__PURE__ */ jsx(LoadMore, { ...loadMoreSlotProps, children: loadMoreLabel })
    ] })
  ] });
});
export {
  Section
};
//# sourceMappingURL=Section.js.map
